import { t as localeText } from "@/shared/lib/translate";
import type { TFunction } from "i18next";

export type ReusableKind =
  | "avatar"
  | "product"
  | "brand"
  | "voice"
  | "preset"
  | "reference_set";

export const REUSABLE_KIND_ORDER = [
  "avatar",
  "product",
  "brand",
  "voice",
  "reference_set",
  "preset",
] as const satisfies readonly ReusableKind[];

export const REUSABLE_KIND_LABEL_KEYS = {
  avatar: "foundation.reusable.kinds.avatar",
  product: "foundation.reusable.kinds.product",
  brand: "foundation.reusable.kinds.brand",
  voice: "foundation.reusable.kinds.voice",
  reference_set: "foundation.reusable.kinds.reference_set",
  preset: "foundation.reusable.kinds.preset",
} as const satisfies Record<ReusableKind, string>;

export function reusableKindLabel(t: TFunction, kind: ReusableKind): string {
  return t(REUSABLE_KIND_LABEL_KEYS[kind]);
}

export function isIdentityReusable<T extends { kind: ReusableKind }>(
  reusable: T,
): reusable is T & { kind: "avatar" | "voice" } {
  return reusable.kind === "avatar" || reusable.kind === "voice";
}

export type ReusableAuthorizationStatus =
  | "pending"
  | "valid"
  | "restricted"
  | "expired"
  | "revoked"
  | "revocation_requested";

export interface ReusableAuthorizationProjection {
  status: Exclude<ReusableAuthorizationStatus, "revocation_requested">;
  expires_at?: string | null;
  revocation_requested_at?: string | null;
}

export function reusableAuthorizationStatus(
  authorization: ReusableAuthorizationProjection,
  now = Date.now(),
): ReusableAuthorizationStatus {
  if (authorization.revocation_requested_at) return "revocation_requested";
  if (authorization.status === "valid" && authorization.expires_at) {
    const expiresAt = Date.parse(authorization.expires_at);
    if (Number.isFinite(expiresAt) && expiresAt <= now) return "expired";
  }
  return authorization.status;
}

export function authorizationCanPrepare(
  authorization: ReusableAuthorizationProjection,
  now = Date.now(),
): boolean {
  return reusableAuthorizationStatus(authorization, now) === "valid";
}

export interface ImmutableReusableSelection {
  reusableID: string;
  versionID: string;
  versionNumber: number;
  kind: ReusableKind;
  name: string;
  displayNameStatus?: DisplayNameStatus;
  role: string;
}

export interface ImmutableReusableOption extends ImmutableReusableSelection {
  versionStatus: string;
  displayNameStatus?: DisplayNameStatus;
  rightsStatus?: ReusableAuthorizationStatus;
  rightsCaptureStatus?: RightsCaptureStatus;
  selectable: boolean;
  unavailableReason?: string;
}

export type RightsCaptureStatus = "exact" | "legacy_ambiguous";
export type DisplayNameStatus = "exact" | "legacy_ambiguous";

export function rightsCaptureLabel(
  t: TFunction,
  status: RightsCaptureStatus,
): string {
  return t(`foundation.reusable.rightsCapture.${status}`);
}

export interface LegacyAmbiguousReusablePin {
  pinStatus: "legacy_ambiguous";
  reusableID: string;
  versionID: null;
  versionNumber: null;
  legacyRootRevision: number | null;
  kind: ReusableKind;
  name: string | null;
  role: string;
}

export interface ProjectContextReusablePinProjection {
  reusable_id: string;
  pin_status: "exact" | "legacy_ambiguous";
  reusable_version_id: string | null;
  version_number: number | null;
  kind: ReusableKind;
  name: string | null;
  display_name_status: DisplayNameStatus | null;
  legacy_root_revision: number | null;
  role: string;
}

export function selectionFromProjectContextPin(
  pin: ProjectContextReusablePinProjection,
): ImmutableReusableSelection | LegacyAmbiguousReusablePin {
  if (pin.pin_status === "legacy_ambiguous") {
    return {
      pinStatus: "legacy_ambiguous",
      reusableID: pin.reusable_id,
      versionID: null,
      versionNumber: null,
      legacyRootRevision: pin.legacy_root_revision,
      kind: pin.kind,
      name: pin.name,
      role: pin.role,
    };
  }
  if (
    !pin.reusable_version_id ||
    pin.version_number === null ||
    !pin.name ||
    !pin.display_name_status
  ) {
    throw new Error(localeText("interface.exact_reusable_pin_is_missing_immutable_version_df3c954c", { defaultValue: "Exact reusable pin is missing immutable version metadata." }));
  }
  return {
    reusableID: pin.reusable_id,
    versionID: pin.reusable_version_id,
    versionNumber: pin.version_number,
    kind: pin.kind,
    name: pin.name,
    displayNameStatus: pin.display_name_status,
    role: pin.role,
  };
}

export function immutableSelectionKey(
  selection: Pick<ImmutableReusableSelection, "versionID">,
): string {
  return selection.versionID;
}

export function selectionFromReusableVersion(
  reusable: { id: string; kind: ReusableKind },
  version: {
    id: string;
    version_number: number;
    display_name: string;
    display_name_status: DisplayNameStatus;
  },
  role = "reference",
): ImmutableReusableSelection {
  return {
    reusableID: reusable.id,
    versionID: version.id,
    versionNumber: version.version_number,
    kind: reusable.kind,
    name: version.display_name,
    displayNameStatus: version.display_name_status,
    role,
  };
}
